import React, { useEffect, useState } from "react";
import { Box, Text, Select } from "@primer/react";
import { SyncIcon } from "@primer/octicons-react";
import { useCommonContext } from "../../../../contexts/common";
import { NotifyHelper } from "../../../../helpers/toast";
import DataTable from "../../../../components/ui/data-table";
import MyButton from "../../../../components/ui/button/Button";
import { phongThiApi } from "../../../../api/phongThiApi";
import { userApi } from "../../../../api/userApi";
import { ex_phongthi } from "../../../../models/response/phong-thi/ex_phongthi";
import { useCommonSelectedHook } from "../../../../hooks/useCommonSelectedHook";

interface GiaoVienChamThiProps {
  kyThiKetThuc: boolean;
  dm_truong_id: number;
  ex_kythi_id: number;
  ex_phongthi_id: number;
  ex_kythi_monthi_id: number;
}

interface GiaoVien {
  id: number;
  full_name: string;
  email: string;
}

const GiaoVienChamThi: React.FC<GiaoVienChamThiProps> = ({
  kyThiKetThuc,
  ex_kythi_monthi_id,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [dsPhongHocs, setDSPhongHocs] = useState<ex_phongthi[]>([]);
  const [dsGiaoViens, setDSGiaoViens] = useState<GiaoVien[]>([]);
  const { translate } = useCommonContext();
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  const loadPhongThi = async () => {
    setIsLoading(true);
    try {
      const res = await phongThiApi.selectByMonThi(ex_kythi_monthi_id);
      if (res.is_success) {
        setDSPhongHocs(res.data);
      } else {
        setDSPhongHocs([]);
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi khi tải dữ liệu");
    }
    setIsLoading(false);
  };

  const loadGiaoVien = async () => {
    try {
      const res = await userApi.SelectTuVanVien(dm_coso_id);
      if (res.is_success) {
        setDSGiaoViens(res.data);
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi khi tải danh sách giáo viên");
    }
  };

  useEffect(() => {
    loadPhongThi();
  }, [ex_kythi_monthi_id]);

  useEffect(() => {
    loadGiaoVien();
  }, []);

  const handleUpdateGiaoVien = async (
    phongThi: ex_phongthi,
    field: keyof ex_phongthi,
    value: number
  ) => {
    if (kyThiKetThuc) return;

    try {
      const updateData: ex_phongthi[] = [
        {
          ...phongThi,
          [field]: value || 0,
        },
      ];

      const res = await phongThiApi.updateGiaoVienChamThi(updateData);

      if (res.is_success) {
        NotifyHelper.Success("Cập nhật thành công");
        loadPhongThi();
      } else {
        NotifyHelper.Error(res.message || "Cập nhật không thành công");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi khi cập nhật");
    }
  };

  return (
    <Box>
      <DataTable
        height="calc(100vh - 300px)"
        data={dsPhongHocs}
        isLoading={isLoading}
        searchEnable
        paging={{ enable: true }}
        actionComponent={
          <Box sx={{ display: "flex", gap: 2 }}>
            {kyThiKetThuc && (
              <Box
                sx={{
                  bg: "danger.emphasis",
                  color: "white",
                  px: 2,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Text sx={{ fontWeight: "bold" }}>
                  {translate("Kỳ thi đã kết thúc")}
                </Text>
              </Box>
            )}
            <MyButton
              text="Tải lại"
              leadingVisual={SyncIcon}
              onClick={loadPhongThi}
            />
          </Box>
        }
        columns={[
          {
            id: "index",
            caption: translate("TT"),
            width: "50px",
            align: "center",
            cellRender: (rowData: any) => (
              <Text>{dsPhongHocs.indexOf(rowData) + 1}</Text>
            ),
          },
          {
            dataField: "ten_phong",
            caption: translate("Tên phòng"),
          },
          {
            dataField: "ten_loai_phong",
            caption: translate("Loại phòng"),
          },
          {
            dataField: "so_thi_sinh",
            caption: translate("Số thí sinh"),
          },
          {
            dataField: "userid_chamthi1",
            caption: translate("Chấm thi 1"),
            cellRender: (row: ex_phongthi) => (
              <Select
                disabled={kyThiKetThuc}
                value={row.userid_chamthi1?.toString() || ""}
                onChange={(e) =>
                  handleUpdateGiaoVien(
                    row,
                    "userid_chamthi1",
                    Number(e.target.value)
                  )
                }
              >
                <Select.Option value="">Chọn giáo viên</Select.Option>
                {dsGiaoViens.map((gv) => (
                  <Select.Option key={gv.id} value={gv.id.toString()}>
                    {`${gv.full_name} (${gv.email})`}
                  </Select.Option>
                ))}
              </Select>
            ),
          },
          {
            dataField: "userid_chamthi2",
            caption: translate("Chấm thi 2"),
            cellRender: (row: ex_phongthi) => (
              <Select
                disabled={kyThiKetThuc}
                value={row.userid_chamthi2?.toString() || ""}
                onChange={(e) =>
                  handleUpdateGiaoVien(
                    row,
                    "userid_chamthi2",
                    Number(e.target.value)
                  )
                }
              >
                <Select.Option value="">Chọn giáo viên</Select.Option>
                {dsGiaoViens.map((gv) => (
                  <Select.Option key={gv.id} value={gv.id.toString()}>
                    {`${gv.full_name} (${gv.email})`}
                  </Select.Option>
                ))}
              </Select>
            ),
          },
          {
            dataField: "userid_chamthi3",
            caption: translate("Chấm thi 3"),
            cellRender: (row: ex_phongthi) => (
              <Select
                disabled={kyThiKetThuc}
                value={row.userid_chamthi3?.toString() || ""}
                onChange={(e) =>
                  handleUpdateGiaoVien(
                    row,
                    "userid_chamthi3",
                    Number(e.target.value)
                  )
                }
              >
                <Select.Option value="">Chọn giáo viên</Select.Option>
                {dsGiaoViens.map((gv) => (
                  <Select.Option key={gv.id} value={gv.id.toString()}>
                    {`${gv.full_name} (${gv.email})`}
                  </Select.Option>
                ))}
              </Select>
            ),
          },
        ]}
      />
    </Box>
  );
};

export default GiaoVienChamThi;
